import Layout from "layout/layout";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AppLog } from "services";
import { useAppSelector } from "store/store";
import { AppRecordJK, AppRecordStatusJK, RequestApi, RouteApi, StatusMsg } from "utils";
import ImageUrlProfile from "../image-url/ImageUrlProfile";
import { Card } from "components/ui/card";
import { logoFlipFlop, profileVerified } from "assets";
import BtnFriendStatus from "../btn-friend-status/BtnFriendStatus";
import { requestSubmitFollowingUser, requestSubmitUnFollowinguUser } from "api-function/requestFriendStatusApi";
import { Hash, ImageUp, Loader, Music, Pause, Play, Users } from "lucide-react";

import Masonry from "react-masonry-css";
import { Skeleton } from "components/ui/skeleton";

import { useDispatch } from "react-redux";

import { Service } from "utils/services";
import { buildStyles, CircularProgressbarWithChildren } from "react-circular-progressbar";
import { setHashtags, setListFriendsUser, setSongs, setPosts, setSearchTextInput } from "store/header";

import ProfileMediaContent from "pages/profile/profile-contents/ProfileMediaContent";
import { Popover, PopoverTrigger } from "components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";

const SuggestionSearch = () => {
  const location = useLocation();
  const { token } = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();

  const searchTextParam: string | null = searchParams.get("search_text");
  const dynamicFlagParam = useRef<string>("");
  const navigate = useNavigate();
  const list = [
    { key: "user", label: "User", sub_title: "Find people and connect instantly", icon: <Users className="text-cyan-500" /> },
    { key: "post", label: "Post", sub_title: "Find the posts that matter to you", icon: <ImageUp className="text-lime-500" /> },
    { key: "song", label: "Song", sub_title: "Find your favorite songs, artists, or albums", icon: <Music className="text-red-500" /> },
    { key: "hashtag", label: "Hashtag", sub_title: "Discover posts by hashtags and trending topics", icon: <Hash className="text-blue-600" /> },
  ];
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [page, setPage] = useState(1); // or use scroll_tag if API uses that
  const { listFriendsUser, songs, hashtags, posts } = useAppSelector((state) => state.header);
  const currentPosts = useAppSelector((state) => state.header.posts);
  const [postId, setPostId] = useState<any>([]);
  const [laodingInitSuggestionSearch, setLaodingInitSuggestionSearch] = useState<boolean>(false);
  const [laodingScroll, setLaodingScroll] = useState<boolean>(false);
  const [hasMoreData, setHasMoreData] = useState<any>();

  const dispatch = useDispatch();
  const [record, setRecord] = useState(new AppRecordJK());

  const getSuggestionResult = async ({ isRefresh = false }: { isRefresh?: boolean } = {}) => {
    setLaodingInitSuggestionSearch(true);
    const updatedRecord = new AppRecordJK({
      ...record,
      loadingStatus: AppRecordStatusJK.loading,
    });
    try {
      if (isRefresh) {
        updatedRecord.loadingStatus = AppRecordStatusJK.loading;
      } else {
        updatedRecord.loadingStatus = AppRecordStatusJK.loadingMore;
      }
      setRecord(updatedRecord);
      const allFetchedPosts: any[] = [];

      let currentPostId = postId;
      const url = `${RouteApi.getSuggestionResult}token=${token}`;
      const data = {
        post_ids: currentPostId,
        search_text: searchTextParam,
        scroll_tag: dynamicFlagParam.current,
      };
      const response = await RequestApi(url, { data });
      if (response.status !== "success") {
        updatedRecord.loadingStatus = AppRecordStatusJK.loaded;
        setRecord(updatedRecord);
      } else {
        if (dynamicFlagParam.current === "post") {
          const newPosts = response.records?.posts || [];
          allFetchedPosts.push(...newPosts);
          setPostId(currentPostId);
          dispatch(setPosts(allFetchedPosts));
        }
        if (dynamicFlagParam.current === "hashtag") {
          dispatch(setHashtags(response.records?.hashtags));
        }
        if (dynamicFlagParam.current === "song") {
          dispatch(setSongs(response.records?.songs));
        }
        currentPostId = response.records.posts?.provider_post_id;
        if (dynamicFlagParam.current === "user") {
          dispatch(setListFriendsUser(response.records.users));
        }
      }
    } catch (error: any) {
      updatedRecord.loadingStatus = AppRecordStatusJK.loaded;
      setRecord(updatedRecord);

      AppLog.show({
        message: `[getSuggestionResult]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      updatedRecord.loadingStatus = AppRecordStatusJK.loaded;
      setRecord(updatedRecord);
      setLaodingInitSuggestionSearch(false);
    }
  };
  const [hasMoreSongs, setHasMoreSongs] = useState(true);
  const [hasMorePosts, setHasMorePosts] = useState(true);

  const fetchMoreData = async () => {
    setIsFetching(true);
    setLaodingScroll(true);
    try {
      const url = `${RouteApi.getSuggestionResult}token=${token}`;
      const data = {
        post_ids: postId,
        search_text: searchTextParam,
        scroll_tag: dynamicFlagParam.current,
        // scroll_tag: "",

        page: page + 1,
      };
      const response = await RequestApi(url, { data });

      if (response.status === "success") {
        setLaodingScroll(false);
        setHasMoreData(response?.records?.users?.next_page_url !== null);

        if (dynamicFlagParam.current === "user") {
          dispatch(
            setListFriendsUser({
              ...listFriendsUser,
              data: [...(listFriendsUser?.data || []), ...response.records.users.data],
            })
          );
        }

        if (dynamicFlagParam.current === "song") {
          const newSongs = response.records.songs.data;

          if (!newSongs || newSongs.length === 0) {
            setHasMoreSongs(false); // 🔴 No more data
          } else {
            dispatch(
              setSongs({
                ...songs,
                data: [...(songs?.data || []), ...newSongs],
              })
            );
          }
        }

        if (dynamicFlagParam.current === "post") {
          const newPosts = response.records.posts;
          if (!newPosts || newPosts.length === 0) {
            setHasMorePosts(false);
          } else {
            dispatch(setPosts([...(currentPosts || []), ...response.records.posts]));
          }

          // dispatch(setPosts((posts: any[]) => [...(posts || []), ...response.records.posts]));
          // setPosts((prev: any[]) => [...(prev || []), ...response.records.posts]);
        }

        if (dynamicFlagParam.current === "hashtag") {
          dispatch(
            setHashtags({
              ...hashtags,
              data: [...(hashtags?.data || []), ...response.records.hashtags.data],
            })
          );
        }
        setPage((prev) => prev + 1);
      }
    } catch (error) {
      setLaodingScroll(false);

      AppLog.show({
        message: `[fetchMoreData]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setLaodingScroll(false);

      setIsFetching(false);
    }
  };
  const [hasScroll, setHasScroll] = useState<boolean>(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleScroll = () => {
    setHasScroll(true);

    // Clear previous timeout if scrolling continues
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Set a timeout to detect scroll stop
    scrollTimeoutRef.current = setTimeout(() => {
      setHasScroll(false);
    }, 500); // Adjust the delay (ms) as needed

    const container = scrollContainerRef.current;
    if (!container || isFetching) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      if (record.canLoadMore()) {
        fetchMoreData();
      }
    }
  };

  useEffect(() => {
    getSuggestionResult();
    setHasMoreSongs(true);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTextParam, dynamicFlagParam.current]);
  useEffect(() => {
    dispatch(setSearchTextInput(searchTextParam));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  for (const [key, value] of searchParams.entries()) {
    if (key !== "search_text") {
      dynamicFlagParam.current = key;
    }
  }

  useEffect(() => {
    if (!location.search.includes("users")) {
      if (!dynamicFlagParam.current) {
        // Manually append '&users' to the current URL
        const newUrl = `${location.pathname}${location.search}&user`;
        navigate(newUrl, { replace: true }); // Update the URL without reloading the page
      }
    }
  }, [location, navigate]);
  const onClickFriend = ({ user, isArray }: { user: any; isArray: boolean }) => {
    const payload = { userId: user?.id };

    if (user?.friend_status === "Friend" || user?.friend_status === "Following") {
      requestSubmitUnFollowinguUser(payload);
    } else {
      requestSubmitFollowingUser(payload);
    }
  };

  const breakpointColumnsObj = {
    default: 6,
    1536: 5,
    1280: 4,
    1024: 3,
    768: 2,
    425: 2,
  };

  const [currentSongUrl, setCurrentSongUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // ---------------------------

  useEffect(() => {
    const checkIfFull = () => {
      const container = scrollContainerRef.current;
      if (
        container &&
        container.scrollHeight <= container.clientHeight &&
        hasMoreSongs && // ✅ Only fetch if there's more
        dynamicFlagParam.current === "song"
      ) {
        fetchMoreData();
      }
    };

    const timeout = setTimeout(checkIfFull, 100);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songs, hasMoreSongs]);
  useEffect(() => {
    const checkIfPostsFull = () => {
      const container = scrollContainerRef.current;

      if (
        container &&
        container.scrollHeight <= container.clientHeight &&
        hasMorePosts && // ✅ Only fetch if more posts are available
        dynamicFlagParam.current === "post"
      ) {
        fetchMoreData();
      }
    };
    const timeout = setTimeout(checkIfPostsFull, 100);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts?.length, hasMorePosts]);

  return (
    <>
      <div className="flex items-center justify-around  w-full mt-2 lg:hidden">
        {list.map((item: any, index: number) => {
          return (
            <div key={index} className=" flex flex-col  mb-2">
              <div
                className={`${
                  dynamicFlagParam.current === item.key ? "border-b-2 border-yellow-300 " : ""
                } gap-3 flex items-center lg:justify-start md:justify-center sm:justify-center px-3 py-2 rounded-md hover:bg-accent hover:border-b-2 border-b-2 border-transparent hover:border-yellow-300 cursor-pointer `}
                onClick={() => {
                  dynamicFlagParam.current = item.key;
                  navigate(`/search?search_text=${searchTextParam}&${dynamicFlagParam.current}`);
                  if (dynamicFlagParam.current === "user") {
                    if (listFriendsUser?.length === 0) {
                      getSuggestionResult();
                    }
                  } else if (dynamicFlagParam.current === "post") {
                    if (posts?.length === 0) {
                      getSuggestionResult();
                    }
                  } else if (dynamicFlagParam.current === "song") {
                    if (songs?.length === 0) {
                      getSuggestionResult();
                    } else {
                    }
                  } else if (dynamicFlagParam.current === "hashtag") {
                    if (hashtags?.length === 0) {
                      getSuggestionResult();
                    }
                  }
                }}
              >
                <Popover>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <PopoverTrigger className="" asChild>
                          <p className="w-7 h-7 mt-1 shrink-0  ">{item.icon}</p>
                        </PopoverTrigger>
                      </TooltipTrigger>
                      <TooltipContent>{item.label}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Popover>
                <div className=" flex-col hidden lg:block md:block">
                  <span className="font-medium leading-none">{item.label}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className=" flex h-[calc(100vh-80px)] w-full relative justify-between overflow-hidden">
        <div className=" sticky top-[80px] left-0 z-0 ">
          <div className="pl-1 md:w-[70px] lg:w-[310px] hidden lg:block  pt-5">
            <div className="space-y-2 flex flex-col justify-between h-[calc(100vh-90px)]">
              <div className="space-y-2 flex flex-col h-[calc(100vh-90px)]">
                {list.map((item: any, index: number) => {
                  return (
                    <div
                      key={index}
                      className={`${
                        dynamicFlagParam.current === item.key ? " border-e-2 border-yellow-300 bg-muted" : ""
                      } gap-3 flex items-center lg:justify-start md:justify-center sm:justify-center px-3 py-2 rounded-md hover:bg-accent border-e-2 border-transparent hover:border-e-2 hover:border-yellow-300  cursor-pointer `}
                      onClick={() => {
                        dynamicFlagParam.current = item.key;
                        navigate(`/search?search_text=${searchTextParam}&${dynamicFlagParam.current}`);
                        if (dynamicFlagParam.current === "user") {
                          if (listFriendsUser?.length === 0) {
                            getSuggestionResult();
                          }
                        } else if (dynamicFlagParam.current === "post") {
                          if (posts?.length === 0) {
                            getSuggestionResult();
                          }
                        } else if (dynamicFlagParam.current === "song") {
                          if (songs?.length === 0) {
                            getSuggestionResult();
                          } else {
                          }
                        } else if (dynamicFlagParam.current === "hashtag") {
                          if (hashtags?.length === 0) {
                            getSuggestionResult();
                          }
                        }
                      }}
                    >
                      <p className="w-7 h-7 mt-1 shrink-0">{item.icon}</p>
                      <div className=" flex-col hidden lg:block">
                        <span className="font-medium leading-none">{item.label}</span>
                        <br />
                        <span className="text-sm text-muted-foreground">{item.sub_title}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        {dynamicFlagParam.current === "user" ? (
          <>
            <div ref={scrollContainerRef} onScroll={handleScroll} className="flex flex-1 h-[calc(100vh-90px)] overflow-y-auto overflow-hidden ">
              {listFriendsUser && (
                <>
                  <div
                    onWheel={(e) => {
                      e.stopPropagation();
                    }}
                    className="w-full"
                  >
                    <div
                      onWheel={(e) => {
                        e.stopPropagation();
                      }}
                      className={`grid grid-cols-2 md:grid-cols-3 w-full   ${window.innerWidth >= 1800 ? "lg:grid-cols-6" : "lg:grid-cols-4"} gap-3 mb-8 p-5`}
                    >
                      {laodingInitSuggestionSearch ? (
                        <>
                          {[...Array(12)].map((_, index) => (
                            <Card key={index} className="rounded-md animate-pulse ">
                              <div className="w-[400px] h-[200px] rounded-t-md" />

                              <div className="p-2 space-y-2">
                                <div className="flex items-center space-x-2">
                                  <div className="w-32 h-5  rounded" />
                                  <div className="w-6 h-6  rounded-full" />
                                </div>
                                <div className="w-40 h-4  rounded" />
                              </div>

                              <div className="grid grid-cols-3 text-center px-4 py-2 gap-2">
                                <div>
                                  <Skeleton className="w-12 h-3 mx-auto  rounded mt-1" />
                                  <Skeleton className="w-12 h-3 mx-auto  rounded mt-1" />
                                </div>
                                <div>
                                  <Skeleton className="w-12 h-3 mx-auto  rounded mt-1" />
                                  <Skeleton className="w-12 h-3 mx-auto  rounded mt-1" />
                                </div>
                                <div>
                                  <Skeleton className="w-12 h-3 mx-auto  rounded mt-1" />
                                  <Skeleton className="w-12 h-3 mx-auto  rounded mt-1" />
                                </div>
                              </div>

                              <div className="p-2">
                                <Skeleton className="w-full h-10  rounded" />
                              </div>
                            </Card>
                          ))}
                        </>
                      ) : (
                        <>
                          {listFriendsUser?.data?.map((user: any, index: number) => {
                            return (
                              <Card key={index} className="rounded-md ">
                                <div
                                  className="cursor-pointer "
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/profile?id=${user?.id}&feed`);
                                  }}
                                >
                                  <ImageUrlProfile className="w-full object-cover h-[200px] rounded-t-md" user={user} isThumbnail={false} />
                                  <div className="p-2">
                                    <div className="flex">
                                      <h1 className="font-semibold text-lg line-clamp-1">{user?.name}</h1>
                                      {user?.is_verified === "Yes" ? (
                                        <div className="flex items-center justify-center pt-0.5 ml-1">
                                          <img src={profileVerified} style={{ width: 24, height: 24 }} alt="" />
                                        </div>
                                      ) : (
                                        <></>
                                      )}
                                    </div>
                                    <p className={`line-clamp-1 ${user?.bio === "" ? "invisible" : ""}`}>{user?.bio !== "" ? user?.bio : "NoBio"}</p>
                                  </div>
                                </div>

                                <div className="grid grid-cols-3 text-center">
                                  <div className="">
                                    <h1 className="font-semibold">{user?.no_of_follower}</h1>
                                    <p className="text-xs">Followers</p>
                                  </div>
                                  <div className="">
                                    <h1 className="font-semibold">{user?.no_of_post}</h1>
                                    <p className="text-xs">Posts</p>
                                  </div>
                                  <div className="">
                                    <h1 className="font-semibold">{user?.no_of_like}</h1>
                                    <p className="text-xs">Likes</p>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 p-2">
                                  <BtnFriendStatus friendStatus={user?.friend_status} onClick={() => onClickFriend({ user, isArray: true })} userType={user?.user_type} />
                                </div>
                              </Card>
                            );
                          })}
                          {laodingScroll && hasMoreData && (
                            <>
                              {[...Array(9)].map((_, index) => (
                                <Card key={index} className="rounded-md animate-pulse ">
                                  <div className="w-[400px] h-[200px] rounded-t-md" />

                                  <div className="p-2 space-y-2">
                                    <div className="flex items-center space-x-2">
                                      <div className="w-32 h-5  rounded" />
                                      <div className="w-6 h-6  rounded-full" />
                                    </div>
                                    <div className="w-40 h-4  rounded" />
                                  </div>

                                  <div className="grid grid-cols-3 text-center px-4 py-2 gap-2">
                                    <div>
                                      <Skeleton className="w-12 h-3 mx-auto  rounded mt-1" />
                                      <Skeleton className="w-12 h-3 mx-auto  rounded mt-1" />
                                    </div>
                                    <div>
                                      <Skeleton className="w-12 h-3 mx-auto  rounded mt-1" />
                                      <Skeleton className="w-12 h-3 mx-auto  rounded mt-1" />
                                    </div>
                                    <div>
                                      <Skeleton className="w-12 h-3 mx-auto  rounded mt-1" />
                                      <Skeleton className="w-12 h-3 mx-auto  rounded mt-1" />
                                    </div>
                                  </div>

                                  <div className="p-2">
                                    <Skeleton className="w-full h-10  rounded" />
                                  </div>
                                </Card>
                              ))}
                            </>
                          )}
                        </>
                      )}
                    </div>

                    {/* <p className="flex items-center justify-center">{laodingScroll && <Loader className="animate-spin text-yellow-400" />}</p> */}
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          ""
        )}
        {dynamicFlagParam.current === "song" ? (
          <div className=" flex flex-col">
            <div ref={scrollContainerRef} onScroll={handleScroll} className="flex flex-1 h-[calc(100vh-90px)] overflow-y-auto overflow-hidden">
              <>
                {laodingInitSuggestionSearch ? (
                  <>
                    <div className=" w-full  h-[calc(100vh-90px)] overflow-y-auto">
                      <div className={`grid grid-cols-1 md:grid-cols-2   ${window.innerWidth >= 1800 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-2 gap-x-2 gap-y-3  mt-2 p-5 `}>
                        {[...Array(12)].map((_, index) => {
                          return (
                            <Card key={index} className="p-2 rounded-md w-full h-[70px]  me-2 relative">
                              <div className="flex gap-3 items-center w-full">
                                <div className="flex items-center space-x-4">
                                  <Skeleton className="h-12 w-12 rounded-full" />
                                  <div className="space-y-2">
                                    <Skeleton className="h-4 w-[200px]" />
                                    <Skeleton className={`h-4  ${window.innerWidth >= 1800 ? "w-[250px]" : "w-[230px]"}`} />
                                  </div>
                                </div>
                                <Skeleton className="h-10 w-10 rounded-full" />
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      {" "}
                      <div
                        className={`grid grid-cols-1 md:grid-cols-2 ${
                          window.innerWidth >= 1800 ? "lg:grid-cols-4" : "lg:grid-cols-3"
                        } gap-2 gap-x-2 gap-y-3  mb-16  min-h-[50px] mt-2 p-5 `}
                      >
                        {songs?.data?.map((item: any, index: number) => {
                          return (
                            <Card key={index} className="p-2 rounded-md w-full me-2 relative">
                              <div className="flex gap-3 items-center w-full">
                                <div className="w-14 h-14">
                                  <img src={item.thumbnail ?? logoFlipFlop} alt={item.name} className="w-14 h-14 rounded-md object-cover" />
                                </div>

                                <div className="flex-1 space-y-1">
                                  <p className="text-md font-semibold line-clamp-1">{item?.sound_title}</p>
                                  <p className="text-muted-foreground text-xs font-semibold line-clamp-1">
                                    {item?.sound_singer || "Unknown"} • {item?.sound_genres || "None"} • {Service.formatSoundDuration(item.sound_duration)} • {item?.usage_count}{" "}
                                    Used
                                  </p>
                                  <p className="text-muted-foreground text-sm font-semibold line-clamp-1">{item?.hashtag}</p>
                                </div>

                                <div className="w-10 h-10 ml-auto">
                                  <CircularProgressbarWithChildren
                                    value={currentSongUrl === item.sound_url && duration ? (currentTime / duration) * 100 : 0}
                                    styles={buildStyles({
                                      pathColor: "hsl(var(--primary))",
                                      trailColor: "hsl(var(--muted))",
                                      strokeLinecap: "round",
                                    })}
                                    strokeWidth={6}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (currentSongUrl === item.sound_url && isPlaying) {
                                          audioRef.current?.pause();
                                        } else if (currentSongUrl === item.sound_url && !isPlaying) {
                                          audioRef.current?.play();
                                        } else {
                                          setCurrentSongUrl(item.sound_url);
                                          setIsPlaying(true);
                                        }
                                      }}
                                      className="text-white bg-black/60 rounded-full w-7 h-7 flex items-center justify-center"
                                    >
                                      {currentSongUrl === item.sound_url && isPlaying ? <Pause size={14} /> : <Play size={14} />}
                                    </button>
                                  </CircularProgressbarWithChildren>
                                </div>
                              </div>
                            </Card>
                          );
                        })}
                        {laodingScroll && hasMoreData && (
                          <>
                            {[...Array(12)].map((_, index) => {
                              return (
                                <Card key={index} className="p-2 rounded-md w-full h-[70px]  me-2 relative">
                                  <div className="flex gap-3 items-center w-full">
                                    <div className="flex items-center space-x-4">
                                      <Skeleton className="h-12 w-12 rounded-full" />
                                      <div className="space-y-2">
                                        <Skeleton className="h-4 w-[200px]" />
                                        <Skeleton className={`h-4  ${window.innerWidth >= 1800 ? "w-[250px]" : "w-[230px]"}`} />
                                      </div>
                                    </div>
                                    <Skeleton className="h-10 w-10 rounded-full" />
                                  </div>
                                </Card>
                              );
                            })}
                          </>
                        )}
                      </div>
                    </div>
                    {currentSongUrl && (
                      <audio
                        ref={audioRef}
                        src={currentSongUrl}
                        autoPlay
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onTimeUpdate={() => {
                          setCurrentTime(audioRef.current?.currentTime || 0);
                        }}
                        onLoadedMetadata={() => {
                          setDuration(audioRef.current?.duration || 0);
                        }}
                        onEnded={() => setIsPlaying(false)}
                        className="hidden"
                      />
                    )}
                  </>
                )}
              </>
            </div>

            {/* <p className="flex items-center justify-center">{laodingScroll && <Loader className="animate-spin text-yellow-400" />}</p> */}
          </div>
        ) : (
          ""
        )}
        {dynamicFlagParam.current === "post" ? (
          <>
            <div ref={scrollContainerRef} onScroll={handleScroll} className=" h-[calc(100vh-90px)] w-full overflow-y-auto items-start justify-start p-5 ">
              <div className="">
                {laodingInitSuggestionSearch ? (
                  <>
                    <Masonry breakpointCols={breakpointColumnsObj} className="my-masonry-grid" columnClassName="my-masonry-grid_column">
                      {[[...Array(8)], {}, {}, {}, {}, {}].map((post: any, index: number) => {
                        return (
                          <Skeleton id={`${post?.id}`} key={index} className="relative group mb-4 break-inside-avoid rounded-xl h-[400px]">
                            <div className="h-[100%] w-full rounded-t-xl relative">
                              <Skeleton className="h-5 w-[35px] bg-gray-200 dark:bg-gray-700 absolute top-2 left-2" />
                            </div>
                          </Skeleton>
                        );
                      })}
                    </Masonry>
                  </>
                ) : (
                  <>
                    <Masonry breakpointCols={breakpointColumnsObj} className="my-masonry-grid" columnClassName="my-masonry-grid_column">
                      {(Array.isArray(posts) && posts.length > 0 ? [...posts, {}, {}, {}, {}, {}, {}, {}, {}] : []).map((post: any, index: number) => {
                        const lastIndex = posts.length;
                        const isPlaceholder = index >= lastIndex;

                        return isPlaceholder ? (
                          <>
                            {hasScroll && (
                              <Skeleton id={`placeholder-${index}`} key={index} className="relative group mb-4 break-inside-avoid rounded-xl h-[400px]">
                                <div className="h-[100%] w-full rounded-t-xl relative">
                                  <Skeleton className="h-5 w-[35px] bg-gray-200 dark:bg-gray-700 absolute top-2 left-2" />
                                </div>
                              </Skeleton>
                            )}
                          </>
                        ) : (
                          // ""
                          <div id={`${post?.id}`} key={index} className="relative group mb-4 break-inside-avoid bg-white rounded-xl">
                            <ProfileMediaContent post={post} />
                          </div>
                        );
                      })}
                    </Masonry>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          ""
        )}
        {dynamicFlagParam.current === "hashtag" ? (
          <>
            <div className="flex flex-col w-full">
              <div ref={scrollContainerRef} onScroll={handleScroll} className="flex flex-1 h-[calc(100vh-90px)] overflow-y-auto items-start justify-start">
                <div className="flex flex-wrap gap-2 p-5">
                  {laodingInitSuggestionSearch ? (
                    <>
                      <div className="flex flex-1 h-[calc(100vh-90px)] overflow-y-auto items-start justify-start">
                        <div className="flex flex-wrap gap-2 p-5">
                          {[...Array(10)].map((_, index) => (
                            <div key={index} className="bg-muted px-3 py-1 rounded-full flex items-center gap-2 animate-pulse">
                              <div className="w-20 h-3 bg-gray-500 rounded" />
                              <div className="w-10 h-3 bg-gray-500 rounded" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {hashtags?.data?.map((tag: any, index: number) => (
                        <div ref={scrollContainerRef} key={index} className="bg-muted  text-sm px-3 py-1 rounded-full flex items-center gap-2">
                          <span>{tag.hashtag ? `#${tag.hashtag}` : <span className="italic text-gray-400"></span>}</span>
                          <span className="text-xs ">{tag.hashtag_count.toLocaleString()}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
              {hashtags?.data?.length <= 0 && <div className="flex items-center justify-center mb-10">No results found</div>}

              <p className="flex items-center justify-center">{laodingScroll && <Loader className="animate-spin text-yellow-400" />}</p>
            </div>
          </>
        ) : (
          ""
        )}
      </div>
    </>
  );
};

export default SuggestionSearch;
